import React, { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';

/**
 * DateOfBirthInput
 * ----------------
 * Friendly date-of-birth entry that replaces the native `<input type="date">`
 * (whose mobile picker is a clunky spin-wheel) with a plain text field that
 * auto-formats MM/DD/YYYY as the parent types. Auto-inserts slashes, strips
 * non-digits, and emits ISO `YYYY-MM-DD` upward so the rest of the form
 * (which already expects ISO strings) needs zero downstream changes.
 *
 * Why this beats both the native picker and 3 separate selects:
 *   - Works identically on iOS, Android, and desktop (no mobile spinner).
 *   - One field, one focus — parents type the date as they would on paper.
 *   - Keyboard hint via `inputMode="numeric"` triggers the number pad on
 *     mobile, so it's as fast as the native picker but without the
 *     "wheel-scrolling-to-1985" frustration.
 *
 * Props mirror a standard <Input/>: `value` (ISO `YYYY-MM-DD` or empty),
 * `onChange(isoValue)`, and any standard data-testid / className for tests.
 */

const formatDisplay = (digitsOnly) => {
    const d = digitsOnly.slice(0, 8);
    if (d.length <= 2) return d;
    if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
    return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};

const displayFromIso = (iso) => {
    if (!iso || typeof iso !== 'string') return '';
    // Accept "YYYY-MM-DD" → "MM/DD/YYYY"
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return '';
    return `${m[2]}/${m[3]}/${m[1]}`;
};

const isoFromDigits = (digitsOnly) => {
    if (digitsOnly.length !== 8) return '';
    const mm = digitsOnly.slice(0, 2);
    const dd = digitsOnly.slice(2, 4);
    const yyyy = digitsOnly.slice(4, 8);
    const month = parseInt(mm, 10);
    const day = parseInt(dd, 10);
    const year = parseInt(yyyy, 10);
    if (Number.isNaN(month) || Number.isNaN(day) || Number.isNaN(year)) return '';
    if (month < 1 || month > 12) return '';
    if (day < 1 || day > 31) return '';
    if (year < 1900 || year > new Date().getFullYear()) return '';
    return `${yyyy}-${mm}-${dd}`;
};

export default function DateOfBirthInput({
    value = '',
    onChange,
    className = '',
    'data-testid': dataTestid,
    placeholder = 'MM/DD/YYYY',
    id,
    ...rest
}) {
    // Local display state so the user can see slashes appear as they type
    // without round-tripping through the parent's ISO string each keystroke.
    const [display, setDisplay] = useState(() => displayFromIso(value));
    // Phase 73q — track when the user has TOUCHED the field so we don't
    // show "incomplete" hints on first focus (would be noisy). Once they
    // blur or type something invalid, we surface a specific helper text.
    const [touched, setTouched] = useState(false);

    // Re-sync from parent (e.g., form reset, prefill from server)
    useEffect(() => {
        const next = displayFromIso(value);
        // Only overwrite when the parent's ISO disagrees with our display.
        // Prevents the cursor from jumping while the user is mid-typing.
        const currentDigits = display.replace(/\D/g, '');
        const currentIso = isoFromDigits(currentDigits);
        if (currentIso !== value) {
            setDisplay(next);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const handleChange = (e) => {
        const raw = e.target.value;
        const digits = raw.replace(/\D/g, '').slice(0, 8);
        const formatted = formatDisplay(digits);
        setDisplay(formatted);
        // Only emit an ISO string when we have a complete + valid date.
        // Otherwise emit '' so the parent's validation can flag a
        // "Please enter a complete date of birth" message at submit time.
        const iso = isoFromDigits(digits);
        if (onChange) onChange(iso);
    };

    // Phase 73q — compute the EXACT inline error message so the parent
    // (the human, not the React parent) sees immediately what's wrong
    // instead of a vague "Each student needs a name and date of birth"
    // toast at submit time. Bug reported by Derek Jones / Kayla, June 2026:
    // entering "09/28/99" (2-digit year) silently failed and the toast
    // claimed the field was empty.
    const digits = display.replace(/\D/g, '');
    let helperText = '';
    if (touched && digits.length > 0 && digits.length < 8) {
        if (digits.length < 4) {
            helperText = 'Keep going — enter month, day, and the full 4-digit year.';
        } else if (digits.length < 6) {
            helperText = 'Almost there — enter the day and full 4-digit year.';
        } else if (digits.length === 6) {
            // This is the EXACT case Derek hit: "09/28/99". The year input
            // is partial. Tell them precisely what's wrong.
            helperText = 'Year must be 4 digits (e.g., enter 1999 not 99).';
        } else {
            helperText = 'Please enter the full 4-digit year.';
        }
    } else if (touched && digits.length === 8) {
        // 8 digits but didn't parse to a valid ISO → bad month/day/year combo.
        const iso = isoFromDigits(digits);
        if (!iso) {
            const year = parseInt(digits.slice(4, 8), 10);
            const month = parseInt(digits.slice(0, 2), 10);
            if (month < 1 || month > 12) {
                helperText = `Month must be 1-12 (you entered ${digits.slice(0, 2)}).`;
            } else if (Number.isNaN(year) || year < 1900 || year > new Date().getFullYear()) {
                helperText = `Year must be between 1900 and ${new Date().getFullYear()}.`;
            } else {
                helperText = 'That date looks off — please double-check the day.';
            }
        }
    }
    const isInvalid = touched && digits.length > 0 && !isoFromDigits(digits);

    return (
        <div className="flex flex-col gap-1">
            <Input
                id={id}
                type="text"
                inputMode="numeric"
                autoComplete="bday"
                value={display}
                onChange={handleChange}
                onBlur={() => setTouched(true)}
                placeholder={placeholder}
                maxLength={10}
                aria-invalid={isInvalid || undefined}
                className={`${className} ${isInvalid ? 'border-rose-500 focus-visible:ring-rose-500' : ''}`.trim()}
                data-testid={dataTestid}
                {...rest}
            />
            {helperText && (
                <p
                    className="text-xs text-rose-600"
                    role="alert"
                    data-testid={dataTestid ? `${dataTestid}-helper` : undefined}
                >
                    {helperText}
                </p>
            )}
        </div>
    );
}
